import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_royal_cradle_007',
  name: 'Royal Cradle Blue',
  occasionType: 'BABY_SHOWER',
  description: 'A sophisticated baby shower invitation featuring white and powder blue watercolor, royal blue floral arrangements, silver framing accents, and a suspended cradle illustration.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'royal', 'cradle', 'floral', 'blue'],
  colorScheme: { primary: '#1e3a8a', accent: '#64748b', bg: '#f0f9ff' },
};

export function RoyalCradleBlue({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables with default fallbacks
  const motherName = baby.motherName || 'Sarah Miller';
  const hostedBy = baby.hostedBy || data.hostName || 'Sarah & David Miller';
  
  // Format Date and Day
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = 'OCTOBER 18, 2026';
  let dayOfWeek = 'SUNDAY';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase();
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '02:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'THE GARDEN ESTATES';
  const venueAddress = baby.venueAddress || data.eventVenue || '456 Rosewood Drive, Beverly Hills, CA';
  const rsvpName = baby.rsvpName || 'Jessica';
  const rsvpPhone = baby.rsvpPhone || '+1 (555) 019-2834';
  const message = baby.message || 'Help us welcome our sweet little prince with love and blessings.';
  const dressCode = baby.dressCode || 'Royal blue or silver grey';

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4 flex flex-col items-center justify-center font-sans select-none">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@300;400;500;600;700&family=Pinyon+Script&display=swap');
        .font-serif-royal { font-family: 'Playfair Display', Georgia, serif; }
        .font-sans-royal { font-family: 'Montserrat', sans-serif; }
        .font-cursive-royal { font-family: 'Pinyon Script', cursive; }
        .font-title-royal { font-family: 'Cinzel', serif; }
        
        .watercolor-bg-blue {
          background: radial-gradient(circle at 50% 50%, #ffffff 0%, #f7faff 30%, #e8f3ff 60%, #bae6fd 100%);
          position: relative;
        }
        
        /* Shimmer Overlay */
        .silver-shimmer {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(224,242,254,0.15) 30%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.3) 100%);
          mix-blend-mode: overlay;
          z-index: 2;
        }

        /* Fine Paper Grain Texture */
        .paper-grain-blue {
          position: absolute;
          inset: 0;
          pointer-events: none;
          mix-blend-mode: multiply;
          opacity: 0.16;
          z-index: 1;
        }

        .silver-foil-text {
          background: linear-gradient(135deg, #475569 0%, #94a3b8 30%, #cbd5e1 50%, #94a3b8 70%, #475569 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0px 1px 1px rgba(255,255,255,0.9));
        }

        .bokeh-pt {
          position: absolute;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(30,58,138,0) 70%);
          filter: drop-shadow(0 0 4px #93c5fd);
          animation: floatBokeh 7s infinite ease-in-out;
        }

        @keyframes floatBokeh {
          0%, 100% { transform: translateY(0) scale(0.8); opacity: 0.2; }
          50% { transform: translateY(-20px) scale(1.2); opacity: 0.9; }
        }
      `}</style>

      {/* Main Card Frame */}
      <div className="relative w-full max-w-[540px] aspect-[1080/1920] shadow-2xl rounded-2xl overflow-hidden watercolor-bg-blue flex flex-col p-10 border-[5px] border-white/85">
        {/* Shimmer & Paper Texture Layers */}
        <div className="silver-shimmer" />
        <svg className="paper-grain-blue" width="100%" height="100%">
          <filter id="linenNoise">
            <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="3" result="noise" />
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.05 0" />
            <feComposite operator="in" in2="SourceGraphic" />
          </filter>
          <rect width="100%" height="100%" filter="url(#linenNoise)" />
        </svg>

        {/* Scattered Sparkles / Bokeh */}
        <div className="bokeh-pt top-[15%] left-[22%]" style={{ animationDelay: '0s' }} />
        <div className="bokeh-pt top-[28%] right-[16%]" style={{ animationDelay: '2.5s' }} />
        <div className="bokeh-pt top-[60%] left-[12%]" style={{ animationDelay: '4.5s' }} />
        <div className="bokeh-pt top-[80%] right-[22%]" style={{ animationDelay: '1.2s' }} />

        {/* Double Silver Victorian Border Frame */}
        <div className="absolute inset-4 border border-[#94a3b8]/25 rounded-2xl pointer-events-none z-10" />
        <div className="absolute inset-5 border-2 border-[#94a3b8]/45 rounded-2xl pointer-events-none z-10">
          
          {/* Top-Left Corner flourish */}
          <svg className="absolute top-[-3px] left-[-3px] w-8 h-8 text-[#94a3b8]/80" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M 2 2 C 10 2, 10 10, 10 10 C 10 14, 6 18, 2 18 C -2 18, -2 14, -2 10 C -2 6, 6 2, 16 2" />
            <circle cx="10" cy="10" r="1.5" fill="currentColor" />
            <circle cx="2" cy="18" r="1" fill="currentColor" />
          </svg>

          {/* Top-Right Corner flourish */}
          <svg className="absolute top-[-3px] right-[-3px] w-8 h-8 text-[#94a3b8]/80 transform rotate-90" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M 2 2 C 10 2, 10 10, 10 10 C 10 14, 6 18, 2 18 C -2 18, -2 14, -2 10 C -2 6, 6 2, 16 2" />
            <circle cx="10" cy="10" r="1.5" fill="currentColor" />
            <circle cx="2" cy="18" r="1" fill="currentColor" />
          </svg>

          {/* Bottom-Left Corner flourish */}
          <svg className="absolute bottom-[-3px] left-[-3px] w-8 h-8 text-[#94a3b8]/80 transform -rotate-90" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M 2 2 C 10 2, 10 10, 10 10 C 10 14, 6 18, 2 18 C -2 18, -2 14, -2 10 C -2 6, 6 2, 16 2" />
            <circle cx="10" cy="10" r="1.5" fill="currentColor" />
            <circle cx="2" cy="18" r="1" fill="currentColor" />
          </svg>

          {/* Bottom-Right Corner flourish */}
          <svg className="absolute bottom-[-3px] right-[-3px] w-8 h-8 text-[#94a3b8]/80 transform rotate-180" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M 2 2 C 10 2, 10 10, 10 10 C 10 14, 6 18, 2 18 C -2 18, -2 14, -2 10 C -2 6, 6 2, 16 2" />
            <circle cx="10" cy="10" r="1.5" fill="currentColor" />
            <circle cx="2" cy="18" r="1" fill="currentColor" />
          </svg>
        </div>

        {/* Top 25%: Large Suspended Cradle Artwork */}
        <div className="absolute top-4 inset-x-0 h-44 pointer-events-none z-10 flex justify-center">
          <svg viewBox="0 0 160 120" className="w-40 h-full">
            {/* Suspended Floral Ropes */}
            <line x1="80" y1="0" x2="80" y2="45" stroke="#94a3b8" strokeWidth="1" />
            <path d="M 80 15 Q 70 25, 60 45" stroke="#475569" strokeWidth="0.75" fill="none" />
            <path d="M 80 15 Q 90 25, 100 45" stroke="#475569" strokeWidth="0.75" fill="none" />
            <circle cx="75" cy="22" r="1.5" fill="#475569" />
            <circle cx="85" cy="28" r="1.5" fill="#475569" />
            
            {/* Hanging Cradle */}
            <path d="M 50 65 Q 80 50, 110 65 Q 120 85, 80 95 Q 40 85, 50 65 Z" fill="none" stroke="#475569" strokeWidth="1.5" />
            <path d="M 55 68 Q 80 55, 105 68" stroke="#94a3b8" strokeWidth="0.75" fill="none" />
            <path d="M 60 76 Q 80 63, 100 76" stroke="#94a3b8" strokeWidth="0.75" fill="none" />
            <path d="M 70 85 Q 80 75, 90 85" stroke="#94a3b8" strokeWidth="0.75" fill="none" />
            <circle cx="80" cy="45" r="3" fill="#94a3b8" />
            
            {/* Sleeping Baby */}
            <circle cx="75" cy="66" r="6" fill="#fed7aa" />
            <path d="M 72 72 Q 80 65, 88 74 Q 85 82, 75 79 Z" fill="#fff" opacity="0.95" />
            <path d="M 74 67 Q 75 68, 76 67" stroke="#3f2305" strokeWidth="0.5" fill="none" />

            {/* Floral details */}
            <circle cx="50" cy="65" r="3.5" fill="#1e3a8a" />
            <circle cx="110" cy="65" r="3.5" fill="#1e3a8a" />
          </svg>
        </div>

        {/* Top-Left and Bottom-Right Floral Arrangements */}
        <div className="absolute top-[-10px] left-[-10px] w-48 h-48 pointer-events-none z-10 opacity-95">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <defs>
              <radialGradient id="blueRoseGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#dbeafe" />
                <stop offset="70%" stopColor="#1e3a8a" />
                <stop offset="100%" stopColor="#172554" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="powderBlueGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#f0f9ff" />
                <stop offset="85%" stopColor="#93c5fd" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="22" cy="22" r="18" fill="url(#blueRoseGrad)" />
            <circle cx="34" cy="16" r="14" fill="url(#powderBlueGrad)" />
            <circle cx="16" cy="34" r="14" fill="url(#blueRoseGrad)" opacity="0.95" />
            <path d="M 38 12 Q 48 5, 45 18 Q 42 20, 38 12" fill="#475569" opacity="0.8" />
            <path d="M 12 38 Q 5 48, 18 45 Q 20 42, 12 38" fill="#475569" opacity="0.8" />
          </svg>
        </div>

        <div className="absolute bottom-[-10px] right-[-10px] w-48 h-48 pointer-events-none z-10 opacity-95">
          <svg viewBox="0 0 100 100" className="w-full h-full transform rotate-180">
            <circle cx="22" cy="22" r="18" fill="url(#blueRoseGrad)" />
            <circle cx="34" cy="16" r="14" fill="url(#powderBlueGrad)" />
            <circle cx="16" cy="34" r="14" fill="url(#blueRoseGrad)" opacity="0.95" />
            <path d="M 38 12 Q 48 5, 45 18 Q 42 20, 38 12" fill="#475569" opacity="0.8" />
            <path d="M 12 38 Q 5 48, 18 45 Q 20 42, 12 38" fill="#475569" opacity="0.8" />
          </svg>
        </div>

        {/* Typography — centered luxury stationery layout */}
        <div className="relative z-20 flex flex-col justify-between h-full pt-48 pb-12 px-4">
          
          {/* Header section */}
          <div className="text-center">
            <p className="font-title-royal text-[8.5px] tracking-[0.48em] text-[#1e3a8a]/60 uppercase mb-8">
              JOIN US TO CELEBRATE A
            </p>

            <h2 className="font-title-royal text-[4rem] text-[#1e3a8a] tracking-[0.14em] font-semibold leading-[1.05] uppercase mb-1 silver-foil-text">
              BABY
            </h2>
            <h2 className="font-title-royal text-[4rem] text-[#1e3a8a] tracking-[0.14em] font-semibold leading-[1.05] uppercase mb-9 silver-foil-text">
              SHOWER
            </h2>

            <svg viewBox="0 0 220 12" className="w-44 h-3 mx-auto mb-8" fill="none">
              <line x1="0" y1="6" x2="88" y2="6" stroke="#1e3a8a" strokeWidth="0.9" opacity="0.45"/>
              <circle cx="110" cy="6" r="2.8" fill="#1e3a8a" opacity="0.55"/>
              <line x1="132" y1="6" x2="220" y2="6" stroke="#1e3a8a" strokeWidth="0.9" opacity="0.45"/>
            </svg>

            <span className="font-title-royal text-[8.5px] text-[#475569]/80 tracking-[0.42em] block mb-8 uppercase">
              IN HONOR OF
            </span>

            <h1 className="font-cursive-royal text-[5.8rem] text-[#1e3a8a] leading-none mb-9 silver-foil-text">
              {motherName}
            </h1>

            <svg viewBox="0 0 220 12" className="w-36 h-3 mx-auto mb-5" fill="none">
              <line x1="0" y1="6" x2="88" y2="6" stroke="#1e3a8a" strokeWidth="0.8" opacity="0.40"/>
              <circle cx="110" cy="6" r="2.2" fill="#1e3a8a" opacity="0.48"/>
              <line x1="132" y1="6" x2="220" y2="6" stroke="#1e3a8a" strokeWidth="0.8" opacity="0.40"/>
            </svg>
          </div>

          {/* Details Section */}
          <div className="flex flex-col items-center gap-5 text-center font-sans-royal flex-grow justify-center">
            
            <div className="w-full max-w-[360px] mx-auto">
              <div className="border-t border-b border-[#1e3a8a]/30 py-4 flex items-center justify-center">
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-royal text-[7px] tracking-[0.32em] text-[#1e3a8a]/60 uppercase block mb-1">DAY</span>
                  <span className="font-title-royal text-[9px] tracking-wider text-[#1e3a8a] font-semibold block">{dayOfWeek}</span>
                  <span className="font-serif-royal text-[11.5px] text-stone-800 font-semibold block mt-0.5">{formattedDate}</span>
                </div>
                <div className="w-px h-10 bg-[#1e3a8a]/20 mx-5 shrink-0" />
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-royal text-[7px] tracking-[0.32em] text-[#1e3a8a]/60 uppercase block mb-1">TIME</span>
                  <span className="font-title-royal text-[9px] tracking-wider text-[#1e3a8a] font-semibold block">AT</span>
                  <span className="font-serif-royal text-[11.5px] text-stone-800 font-semibold block mt-0.5">{eventTime}</span>
                </div>
              </div>
            </div>

            <div className="w-full max-w-[340px] mx-auto text-center">
              <span className="font-title-royal text-[7px] tracking-[0.35em] text-[#1e3a8a]/60 uppercase block mb-1.5">VENUE</span>
              <span className="font-title-royal text-[10px] tracking-[0.18em] text-[#1e3a8a] font-semibold block uppercase">{venueName}</span>
              <span className="font-sans-royal text-[9px] text-stone-600 block leading-relaxed mt-1">{venueAddress}</span>
            </div>

            <div
              role="button"
              aria-label="RSVP Now"
              onClick={() => setShowRsvpModal(true)}
              className="cursor-pointer group"
            >
              <div
                className="px-9 py-2.5 rounded-full border border-[#1e3a8a]/30 bg-gradient-to-r from-[#f0f9ff] via-white to-[#f0f9ff] group-hover:from-[#e0f2fe] group-hover:to-[#e0f2fe] transition-all duration-200"
                style={{ boxShadow: '0 2px 14px rgba(30,58,138,0.06)' }}
              >
                <span className="font-title-royal text-[8.5px] tracking-[0.38em] text-[#1e3a8a] font-semibold uppercase block text-center">RSVP</span>
                <span className="font-sans-royal text-[9.5px] text-stone-700 font-medium block text-center mt-0.5">{rsvpName} · {rsvpPhone}</span>
              </div>
            </div>

            {dressCode && (
              <div
                className="inline-block px-5 py-1.5 rounded-full border border-[#1e3a8a]/30"
                style={{ background: 'rgba(240,249,255,0.60)' }}
              >
                <span className="font-title-royal text-[7.5px] tracking-[0.28em] text-[#1e3a8a] uppercase font-semibold">
                  DRESS CODE &ensp;·&ensp; {dressCode}
                </span>
              </div>
            )}
          </div>

          {/* Bottom Section */}
          <div className="text-center pt-4">
            <span className="font-serif-royal text-[10px] text-stone-600 block leading-relaxed max-w-[300px] mx-auto mb-3">
              "{message}"
            </span>

            <svg viewBox="0 0 180 10" className="w-36 h-2 mx-auto mb-3" fill="none">
              <line x1="0" y1="5" x2="72" y2="5" stroke="#1e3a8a" strokeWidth="0.7" opacity="0.4"/>
              <circle cx="90" cy="5" r="2" fill="#1e3a8a" opacity="0.5"/>
              <line x1="108" y1="5" x2="180" y2="5" stroke="#1e3a8a" strokeWidth="0.7" opacity="0.4"/>
            </svg>

            <span className="font-serif-royal text-[11px] text-[#1e3a8a]/80 block tracking-wide">
              Hosted with love by
            </span>
            <span className="font-title-royal text-[9.5px] tracking-[0.28em] text-stone-800 font-semibold block mt-1 uppercase">
              {hostedBy}
            </span>
          </div>

        </div>

      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          <div className="relative w-full max-w-md bg-stone-900 border border-white/10 rounded-3xl p-6 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-serif text-white font-semibold">Are You Attending?</h3>
              <p className="text-white/60 text-sm mt-1">Please RSVP to let us know if you're coming!</p>
            </div>
            
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: RoyalCradleBlue });
